import {
  CHAT_AUDIO_SEND_MIME,
  effectiveFileMime,
  isChatAudioMime,
  MAX_CHAT_AUDIO_BYTES,
} from 'utils/chatAttachmentLimits'

/** Formatos reproduzidos de forma confiável em iOS, Android e web. */
export function isCrossPlatformChatAudioMime(mime: string): boolean {
  const base = mime.split(';')[0]?.trim().toLowerCase() ?? ''
  return (
    base === 'audio/mpeg' ||
    base === 'audio/mp4' ||
    base === 'audio/x-m4a' ||
    base === 'audio/m4a' ||
    base === 'audio/aac'
  )
}

export function needsChatAudioConversion(mime: string): boolean {
  const base = mime.split(';')[0]?.trim().toLowerCase() ?? ''
  if (!isChatAudioMime(base)) return false
  return !isCrossPlatformChatAudioMime(base)
}

function getAudioContext(): AudioContext {
  const Ctx =
    typeof window !== 'undefined'
      ? window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext
      : undefined
  if (!Ctx) {
    throw new Error('AudioContext indisponível neste navegador.')
  }
  return new Ctx()
}

function mixToMono(buffer: AudioBuffer): Float32Array {
  if (buffer.numberOfChannels === 1) {
    return buffer.getChannelData(0)
  }
  const left = buffer.getChannelData(0)
  const right = buffer.getChannelData(1)
  const mono = new Float32Array(left.length)
  for (let i = 0; i < left.length; i++) {
    mono[i] = (left[i]! + right[i]!) / 2
  }
  return mono
}

function floatTo16BitPcm(input: Float32Array): Int16Array {
  const output = new Int16Array(input.length)
  for (let i = 0; i < input.length; i++) {
    const sample = Math.max(-1, Math.min(1, input[i]!))
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
  }
  return output
}

type LamejsGlobal = {
  Mp3Encoder: new (
    channels: number,
    sampleRate: number,
    kbps: number
  ) => {
    encodeBuffer(left: Int16Array, right?: Int16Array): Int8Array
    flush(): Int8Array
  }
}

/**
 * `@breezystack/lamejs` em vez de `lamejs`.
 *
 * O pacote original só funciona como `<script>` clássico: o `lame.min.js`
 * declara `function lamejs(){…}`, chama a função no fim e pendura `Mp3Encoder`
 * no objeto dela. Sob bundler, `import()` processa como módulo, nenhum global é
 * criado e o `default` vem vazio — então o encoder nunca ficava disponível. O
 * entry principal da 1.2.1 tem problema próprio (`MPEGMode is not defined`).
 *
 * O fork mantido existe para exatamente isso e exporta `Mp3Encoder` de verdade.
 */
async function loadLamejsMp3Encoder(): Promise<LamejsGlobal['Mp3Encoder']> {
  const mod = await import('@breezystack/lamejs')
  const Mp3Encoder =
    (mod as unknown as LamejsGlobal).Mp3Encoder ??
    (mod as unknown as { default?: LamejsGlobal }).default?.Mp3Encoder
  if (!Mp3Encoder) {
    throw new Error('Encoder MP3 indisponível.')
  }
  return Mp3Encoder
}

async function encodeBlobToMp3(blob: Blob): Promise<Blob> {
  const Mp3Encoder = await loadLamejsMp3Encoder()

  const arrayBuffer = await blob.arrayBuffer()
  const audioContext = getAudioContext()
  if (audioContext.state === 'suspended') {
    await audioContext.resume()
  }
  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0))
    const sampleRate = audioBuffer.sampleRate
    const mono = mixToMono(audioBuffer)
    const samples = floatTo16BitPcm(mono)
    const mp3encoder = new Mp3Encoder(1, sampleRate, 128)
    const blockSize = 1152
    const mp3Data: Int8Array[] = []

    for (let i = 0; i < samples.length; i += blockSize) {
      const chunk = samples.subarray(i, i + blockSize)
      const mp3buf = mp3encoder.encodeBuffer(chunk)
      if (mp3buf.length > 0) {
        mp3Data.push(mp3buf)
      }
    }

    const end = mp3encoder.flush()
    if (end.length > 0) {
      mp3Data.push(end)
    }

    let total = 0
    for (const part of mp3Data) {
      total += part.length
    }
    const merged = new Uint8Array(total)
    let offset = 0
    for (const part of mp3Data) {
      merged.set(
        new Uint8Array(part.buffer, part.byteOffset, part.byteLength),
        offset
      )
      offset += part.length
    }

    return new Blob([merged], { type: CHAT_AUDIO_SEND_MIME })
  } finally {
    void audioContext.close()
  }
}

function buildMp3Filename(originalName?: string): string {
  const base = (originalName || '').replace(/\.[^.]+$/, '').trim()
  const stamp = Date.now()
  if (base && !base.toLowerCase().includes('audio')) {
    return `${base.slice(0, 80)}-${stamp}.mp3`
  }
  return `audio-${stamp}.mp3`
}

/**
 * MP4 fragmentado: `ftyp`, um `moov` mínimo e então pares `moof`/`mdat`.
 *
 * É o que o `MediaRecorder` do navegador produz quando grava `audio/mp4`, e o
 * `MediaPlayer` do Android **não reproduz** — ele espera MP4 progressivo, com a
 * tabela de amostras completa no `moov`. O AVFoundation do iOS aceita os dois,
 * então o sintoma é 'áudio gravado na web não toca no Android'.
 *
 * Detectar pelo conteúdo, e não pelo mime, cobre também arquivo escolhido do
 * disco que por acaso seja fragmentado — o mime dos dois é `audio/mp4`.
 */
export function isFragmentedMp4(bytes: Uint8Array): boolean {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const tipo = (off: number) =>
    String.fromCharCode(
      bytes[off + 4],
      bytes[off + 5],
      bytes[off + 6],
      bytes[off + 7]
    )
  let off = 0
  // Um punhado de boxes basta: o primeiro `moof` aparece logo depois do `moov`.
  for (let i = 0; i < 12 && off + 8 <= bytes.length; i += 1) {
    const size = view.getUint32(off)
    const t = tipo(off)
    if (t === 'moof') return true
    // `size` 0 significa 'vai até o fim'; 1 significa tamanho estendido em 64
    // bits. Nos dois casos não há mais box de topo para inspecionar.
    if (size <= 1) return false
    off += size
  }
  return false
}

function isMp4Mime(mime: string): boolean {
  const base = mime.split(';')[0]?.trim().toLowerCase() ?? ''
  return (
    base === 'audio/mp4' ||
    base === 'audio/m4a' ||
    base === 'audio/x-m4a' ||
    base === 'video/mp4'
  )
}

/** Converte WebM/OGG/etc. para MP3 antes do envio ou preview. */
export async function convertChatAudioForSend(
  input: Blob,
  options?: { filename?: string; mimeType?: string; force?: boolean }
): Promise<File> {
  const mime = options?.mimeType || input.type || 'application/octet-stream'
  if (!options?.force && !needsChatAudioConversion(mime)) {
    const name =
      options?.filename ||
      (input instanceof File ? input.name : buildMp3Filename())
    return input instanceof File
      ? input
      : new File([input], name, { type: mime.split(';')[0] })
  }

  const mp3Blob = await encodeBlobToMp3(input)
  if (mp3Blob.size > MAX_CHAT_AUDIO_BYTES) {
    throw new Error(
      'Áudio convertido excede 5 MB. Grave uma mensagem mais curta.'
    )
  }

  return new File([mp3Blob], buildMp3Filename(options?.filename), {
    type: CHAT_AUDIO_SEND_MIME,
  })
}

/** Quanto do começo do arquivo basta para achar o primeiro `moof`. */
const MP4_SNIFF_BYTES = 64 * 1024

export async function normalizeChatAudioFileIfNeeded(
  file: File
): Promise<File> {
  const mime = effectiveFileMime(file)

  if (needsChatAudioConversion(mime)) {
    return convertChatAudioForSend(file, {
      filename: file.name,
      mimeType: mime,
    })
  }

  // `audio/mp4` está na lista de formatos cross-platform, e o codec de fato é.
  // O container é que pode não ser: o do `MediaRecorder` vem fragmentado.
  if (isMp4Mime(mime)) {
    const head = new Uint8Array(
      await file.slice(0, MP4_SNIFF_BYTES).arrayBuffer()
    )
    if (isFragmentedMp4(head)) {
      return convertChatAudioForSend(file, {
        filename: file.name,
        mimeType: mime,
        force: true,
      })
    }
  }

  return file
}
